import { NextPage } from 'next'
import Head from 'next/head'

import ServiceHero from '~/components/organisms/ServiceHero'
import Issue from '~/components/organisms/Issue'
import AdFeature from '~/components/organisms/AdFeature'
import Example from '~/components/organisms/Example'
import Solution from '~/components/organisms/Solution'

const comment = [
  'オンライン集客をしたいけれど、何から手をつけていいか分からない…',
  `学校の校門前でのチラシ配布、ポスティング、DM、テレアポなど、
  既存の集客方法に限界を感じている…`,
  'GoogleやYahooの検索画面に、少額で広告を出したい…',
  'デジタルマーケティングの専門家に、オンライン集客のプランニングをして欲しい…',
  '教育業界やスクール事業の特性が分かっているスタッフに運用をお願いしたい…',
]

const strength = [
  {
    index: '01',
    title: '学習塾・教室業の広告には専門知識が必要',
    description:
      '一般的なウェブ広告とは異なり、学習塾・教室業の集客は生徒・保護者それぞれへのアプローチを行う必要があります。当然、子どもと親ではユーザーのインサイトも全く異なります。eduadには学習塾や教室業の集客のベテランが在籍しています。大手の広告代理店に委託したら「担当者が教育業界の特性を知らなかった」「教育系案件の経験が浅かった」というケースもありますが、eduadならそんな心配はありません。',
    src: '/professional.jpg',
  },
  {
    index: '02',
    title: `月額30,000円から少額で広告運用`,
    description:
      'eduadでは月額30,000円〜と少額での広告運用が可能です。初期設定、クリエイティブ制作から運用まで専任の担当者がワンストップで行うため高い成果が期待できます。広告代理店では、通常20%の運用管理費やレポート費が発生するケースが一般的です。そこまでコストがかけられない、スモールスタートしたい、などお気軽にご相談ください。',
    src: '/onestop.jpg',
  },
  {
    index: '03',
    title: '広告運用・デジタルマーケティングの有資格者がサポートするから安心',
    description:
      'eduadでは広告運用・デジタルマーケティングの有資格者がサポートを行います。「スキルマーケットでの発注を検討しているが、担当者の顔が見えなくて不安」などお困りの方、ご発注前にぜひご相談ください。',
    src: '/credit.jpg',
  },
]

const example = [
  {
    needs: 'とにかく教室への問い合わせ件数を増やしたい！',
    solution:
      'まずはGoogleリスティング広告を実施！確度の高いニーズを持ったユーザーに配信！',
  },
  {
    needs: '新しく教室をオープンしたので認知を拡げたい！',
    solution:
      'Google・Yahooでディスプレイ広告を実施！対象地域・年齢を絞って、生徒だけではなく保護者層へも積極的にアプローチ！',
  },
  {
    needs: 'ウェブ広告運用をゆくゆくは自教室で行いたい！',
    solution:
      'まずは初期設定・運用代行を担当者が実施！その後、管理画面の使い方・広告配信の考え方を一からレクチャー！',
  },
  {
    needs: 'オンライン集客戦略全般を支援してほしい！',
    solution:
      '教室の集客増加につながるオンライン集客施策を立案！ウェブサイト構築・改修・LPOから広告戦略までワンストップでご支援！',
  },
]

const title = 'eduadのマーケティング支援の流れ'

const subtitle =
  'お問い合わせをいただいた後、初回面談にて、貴社の教室運営における課題、現在のお取り組み状況をヒアリングさせていただき、解決策のご提案をさせていただきます。'

const colorProps = [
  {
    color: '#00bab3',
    number: 1,
    flow: 'お打ち合わせ',
    title: 'ヒアリング・現状把握',
    description:
      'オンラインにてお打ち合わせをさせていただき、お客様がお持ちの課題についてお伺いします。マーケティング施策・ウェブ広告運用に関することはもちろん、集客や教室運営においての課題・お悩みもお伺いできればと思います。',
  },
  {
    color: '#06a0cf',
    number: 2,
    flow: '提案',
    title: 'ご提案作成',
    description:
      'ヒアリングでご教示いただいた課題や目標、「コストパフォーマンス(CPA)を重視したい」「地域での認知を拡大させたい」などのご要望に応じたコミュニケーション戦略プランをご提案します。',
  },
  {
    color: '#068fcf',
    number: 3,
    flow: '契約',
    title: 'ご契約',
    description:
      'ご提案内容・料金にご納得いただけましたら、ご契約の手続きを進めて参ります。',
  },
  {
    color: '#167296',
    number: 4,
    flow: '初期設定',
    title: 'アカウント設定・クリエイティブ制作・入稿',
    description:
      '広告配信についての初期設定を行います。また、コミュニケーション戦略に基づいて、クリエイティブ制作やキーワード設定なども行います。※広告タグの設置など、ご対応いただく点がいくつかございます。',
  },
  {
    color: '#165c96',
    number: 5,
    flow: '配信開始',
    title: '広告配信開始',
    description:
      'ご要望のタイミングで広告配信を開始いたします。ご契約後、最短で２週間程度で開始できます。',
  },
  {
    color: '#164d96',
    number: 6,
    flow: 'レポート',
    title: '月次報告会',
    description:
      '広告の配信状況・各種指標・改善ポイントについて、月１回オンライン報告会を行います。',
  },
  {
    color: '#0e3296',
    number: 7,
    flow: 'インハウス化支援',
    title: '中長期的なインハウス化の支援',
    description:
      '広告運用が安定したタイミングで、ご要望に応じて、インハウス化の支援を開始します。管理画面の見方、操作方法、マーケティングの考え方なと、全般的な支援も可能です。',
  },
]

const advertising: NextPage = () => {
  return (
    <>
      <Head>
        <title>
          ウェブ広告運用・オンライン集客・マーケティング支援 | eduad
        </title>
        <meta
          name='description'
          content='eduad-エデュアド-は教育・スクール運営に特化したウェブマーケティング支援を行うプロフェッショナル技術者集団です。Google・Yahooのリスティング検索広告・ディスプレイ広告の初期設定、運用、インハウス化までサポート'
        />
      </Head>
      <ServiceHero route='advertising' />
      <Issue
        title='学習塾・教室業のウェブ広告なら、eduadにお任せください'
        comment={comment}
      />
      <AdFeature
        title='eduadの行う学習塾・教室業特化型のウェブ広告の特長'
        strength={strength}
      />
      <Example example={example} />
      <Solution title={title} subtitle={subtitle} colorProps={colorProps} />
    </>
  )
}

export default advertising
